import React, { useEffect } from "react";
import { TextField, Button, MenuItem, Card, CardContent, Dialog } from "@mui/material";
import { Box, Typography } from "@mui/material";
import axios from "axios";

const AddProductForm = ({ editingProduct, onUpdateProduct }) => {
  const [categories, setCategories] = React.useState([]);
  const [filteredProducts, setFilteredProducts] = React.useState([]); // State for filtered products
  const [successDialogOpen, setSuccessDialogOpen] = React.useState(false);

  const [formData, setFormData] = React.useState({
    category: "",
    name: "",
    price: "",
    vat: "",
    company: "",
    discount: "",
    count: "",
    previous: "",
    date: "",
  });


  // Populate form if editing an existing product
  useEffect(() => {
    if (editingProduct) {
      setFormData(prevData => ({
        ...prevData,   // Preserve existing form values
        ...editingProduct // Merge existing product values
      }));
    }
  }, [editingProduct]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get("https://enzo-matr.onrender.com/api/categories");
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchFilteredProducts = async () => {
      if (formData.category) {
        try {
          const response = await axios.get(`https://enzo-matr.onrender.com/api/products?category=${formData.category}`);
          if (response.status === 200) {
            setFilteredProducts(response.data.filter(product => product.category === formData.category));
          } else {
            setFilteredProducts([]);
          }
        } catch (error) {
          console.error("Error fetching filtered products:", error);
          setFilteredProducts([]);
        }
      } else {
        setFilteredProducts([]);
      }
    };

    fetchFilteredProducts();
  }, [formData.category]); // Fetch products when the category changes

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));

    if (name === "name") {
      onUpdateProduct(value); // Send selected product name to UpdateProductPage
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Ensure editingProduct exists and has an _id field
    const productId = editingProduct?._id;
    if (!productId) {
      alert("Error: No product selected for updating.");
      return;
    }

    try {
      const response = await fetch(`https://enzo-matr.onrender.com/api/products/${productId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),  // Use the existing formData state
      });

      const result = await response.json();
      console.log("Product updated:", result);

      if (response.ok) {
        setSuccessDialogOpen(true); // Show success dialog
        onUpdateProduct(result); // Ensure parent component updates state
        // window.location.reload(); // (Optional: Remove this for better UX)
      } else {
        alert("Failed to update product");
      }
    } catch (error) {
      console.error("Error updating product:", error);
      alert("Error updating product");
    }
  };

  return (
    <Card
      sx={{
        width: { xs: "98vw", sm: "90vw", md: "410px" },
        height: { xs: "auto", md: "515px" },
        margin: { xs: "10px auto", md: "auto" },
        mt: { xs: 2, md: 3 },
        boxShadow: 2,
        borderRadius: "10px",
        marginLeft: { xs: "auto", md: "1060px" },
        marginTop: { xs: "10px", md: "-510px" },
        p: { xs: 1, md: 0 }
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          p: { xs: 1, md: 2 }
        }}
      >
        <Typography variant="h6" align="center" sx={{ fontFamily: "Open Sans, sans-serif", fontWeight: "bold", fontSize: "26px", marginTop: "-12px", marginBottom: "0px" }} gutterBottom>
          {editingProduct ? "Edit Product" : "UPDATE PRODUCT"}
        </Typography>

        <Box display="flex" flexDirection="column" gap={1} sx={{ flex: 1, overflow: "auto" }}>

          <TextField
            select
            name="category"
            value={formData.category || ""} // Ensures default empty value
            onChange={handleChange}
            fullWidth
            size="small"
            variant="outlined"
            displayEmpty
            SelectProps={{
              displayEmpty: true, // Ensures "Select Category" is visible onload
            }}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: "8px" },
              "& .MuiSelect-select": {
                fontWeight: formData.category ? "normal" : "bold", // Bold when no category is selected
                color: formData.category ? "black" : "black", // Different color for placeholder
              },
            }}
          >
            {/* Bold Placeholder Option */}
            <MenuItem value="" disabled>
              Select Category
            </MenuItem>

            {/* Categories from API */}
            {categories.map((cat) => (
              <MenuItem key={cat._id} value={cat.name}>
                {cat.name}
              </MenuItem>
            ))}
          </TextField>

          {/* Product Name Field */}
          <TextField
            select
            name="name"
            value={formData.name || ""}
            onChange={handleChange}
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Select Product" // Placeholder added
            SelectProps={{
              displayEmpty: true, // Ensures placeholder is visible when no product is selected
            }}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: "8px" },
              "& .MuiSelect-select": {
                fontWeight: formData.name ? "normal" : "bold", // Bold when no product is selected
                color: formData.name ? "black" : "gray", // Gray color for placeholder
              },
            }}
          >
            <MenuItem value="">
              <em>Select Product</em> {/* Placeholder option */}
            </MenuItem>
            {filteredProducts.map((product) => (
              <MenuItem key={product._id} value={product.name}>
                {product.name}
              </MenuItem>
            ))}
          </TextField>

          {/* Date and Time Field */}
          <TextField
            name="date"
            label="Select Date"
            type="date"
            value={formData.date || ""}
            onChange={handleChange}
            fullWidth
            size="small"
            variant="outlined"
            InputLabelProps={{
              shrink: true, // Ensures label stays above when date is selected
            }}
            inputProps={{
              placeholder: formData.date ? "" : "Select Date", // Placeholder appears when empty
            }}
            onFocus={(e) => e.target.showPicker()} // Opens calendar on click
          />





          <TextField
            name="count"
            value={formData.count}
            onChange={handleChange}
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Enter Count"

            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" }, "& .MuiInputBase-input::placeholder": { color: "black", opacity: 1, fontFamily: "Arial,sans-serif", fontWeight: "bold" } }}
          />

          {/* VAT Field */}
          <TextField
            name="vat"
            value={formData.vat}
            onChange={handleChange}
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Enter Product VAT"

            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" }, "& .MuiInputBase-input::placeholder": { color: "black", opacity: 1, fontFamily: "Arial,sans-serif", fontWeight: "bold" } }}
          />

          {/* Product Price Field */}
          <TextField
            name="price"
            value={formData.price}
            onChange={handleChange}
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Enter  Price"

            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" }, "& .MuiInputBase-input::placeholder": { color: "black", opacity: 1, fontFamily: "Arial,sans-serif", fontWeight: "bold" } }}
          />


          {/* Discount Field */}
          <TextField
            name="discount"
            value={formData.discount}
            onChange={handleChange}
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Enter Product Discount"

            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" }, "& .MuiInputBase-input::placeholder": { color: "black", opacity: 1, fontFamily: "Arial,sans-serif", fontWeight: "bold" } }}
          />



          {/* Average Count Field */}
          <TextField
            name="previous"
            value={formData.previous}
            onChange={handleChange}
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Enter Product Avg Count"

            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" }, "& .MuiInputBase-input::placeholder": { color: "black", opacity: 1, fontFamily: "Arial,sans-serif", fontWeight: "bold" } }}
          />





          {/* Submit Button */}
          <Button
            variant="contained"
            fullWidth
            sx={{
              mt: "1px",
              fontFamily: "'Times New Roman', Times, serif",
              fontWeight: "bold",
              fontSize: "25px",
              height: "70px",
              borderRadius: 2,
              backgroundColor: "#262B2F",
            }}
            onClick={handleSubmit}
          >
            {editingProduct ? "Update Product" : "Update Product"}
          </Button>
        </Box>
      </CardContent>

      <Dialog open={successDialogOpen} onClose={() => setSuccessDialogOpen(false)}>
        <Card sx={{ borderRadius: 3, p: 2, minWidth: 300, textAlign: "center" }}>
          <Box display="flex" flexDirection="column" alignItems="center" mt={1}>
            {/* Green Tick SVG */}
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="12" fill="#4caf50"/>
              <path d="M7 13l3 3 7-7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <Typography variant="h6" align="center" sx={{ fontWeight: "bold", color: "#4caf50", mt: 2 }}>
              Product updated successfully!
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                setSuccessDialogOpen(false);
                window.location.reload(); // Refresh the page after closing dialog
              }}
              sx={{ background: "#4caf50", color: "white", fontWeight: "bold", mt: 2 }}
            >
              OK
            </Button>
          </Box>
        </Card>
      </Dialog>
    </Card>
  );
};

export default AddProductForm;